"use client";

import { useTurnstile } from "@/hooks/useTurnstile";
import { useCoAnalystSignup } from "./useCoAnalystSignup";

/**
 * Sidebar "Get Access" card with an inline, email-only sign-up form.
 *
 * Submit + redirect behaviour lives in `useCoAnalystSignup`, shared with the
 * mobile bottom banner; both post to `/api/leads/trial` behind Cloudflare
 * Turnstile. On success the visitor lands on the platform signup page with the
 * email pre-filled, so they only need to set a name and password.
 */
export default function CoAnalystAccessCard() {
  const { containerRef, token, reset } = useTurnstile({ theme: "dark" });
  const { email, setEmail, submitting, redirecting, error, fieldError, handleSubmit } =
    useCoAnalystSignup({
      useCase: "Co-Analyst Access",
      token,
      resetTurnstile: reset,
    });

  return (
    <div className="relative overflow-hidden rounded-2xl bg-[#0a0a0a] text-white p-6">
      <div className="relative z-10">
        <p className="text-xl font-[family-name:var(--font-display)] tracking-[-0.02em] leading-tight text-white mb-5">
          Try Hudson Labs free for 14 days.
        </p>

        {redirecting ? (
          <p className="text-sm text-white/80 text-center py-4">
            You&apos;re all set. Taking you to sign up&hellip;
          </p>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-3">
            <div>
              <input
                type="email"
                placeholder="name@company.com"
                aria-label="Work email"
                required
                disabled={submitting}
                autoComplete="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className={`w-full rounded-lg bg-white/10 border px-3.5 py-2.5 text-sm text-white placeholder:text-white/40 outline-none transition-colors ${
                  fieldError
                    ? "border-red-400/70 focus:border-red-400"
                    : "border-white/15 focus:border-violet-500"
                }`}
              />
              {fieldError && <p className="text-xs text-red-300 mt-1">{fieldError}</p>}
            </div>

            {/* Turnstile widget */}
            <div ref={containerRef} />

            {error && (
              <p className="text-sm text-red-300" role="alert">
                {error}
              </p>
            )}

            <button
              type="submit"
              disabled={submitting}
              className="group w-full inline-flex items-center justify-center gap-2 rounded-lg bg-violet-600 hover:bg-violet-700 disabled:opacity-50 disabled:cursor-not-allowed px-5 py-3 text-sm font-medium text-white transition-colors"
            >
              {submitting ? "Sending…" : "Start free trial"}
              {!submitting && (
                <svg
                  className="w-3.5 h-3.5 transition-transform group-hover:translate-x-0.5"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth={2}
                  aria-hidden="true"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" d="M5 12h14M13 5l7 7-7 7" />
                </svg>
              )}
            </button>
          </form>
        )}

        <p className="text-[11px] text-white/50 mt-4">
          No charge for 14 days. Then{" "}
          <span className="font-semibold text-white">$119/month. Cancel anytime.</span>
        </p>
        <p className="text-[11px] text-white/40 mt-1">
          Billed monthly, no annual contract. We&apos;ll email you before your first charge.
        </p>

        <hr className="my-4 border-0 border-t border-white/10" />

        <p className="text-[11px] text-white/50 text-center">
          Trusted by funds managing <span className="font-semibold text-white">$600B+</span> in AUM
        </p>
      </div>
    </div>
  );
}
